import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchUserChips } from '../../../redux/credibility/credibilityChipsDisplay/credibilityChipActions';
import {
  fetchUserHistoryPosts, loadMoreUserHistoryPosts,
  fetchUserGroupHistoryPosts, loadMoreUserGroupHistoryPosts,
} from '../../../redux/posts/postActions';
import { logAnalyticsEvent } from '../../../redux/analytics/analyticsEventsAPI';
import { tierColors } from '../../../utils/credibilityTiers';
import {
  Shield, TrendingUp, TrendingDown, Clock, CheckCircle, XCircle,
  Target, Activity, Eye, Calendar, FileText, ExternalLink,
  Minus, Search, SortAsc, SortDesc, MessageSquare, Heart,
  TrendingUpIcon, TrendingDownIcon, BarChart3, Timer, Award, Brain, Users
} from 'lucide-react';
import { 
  WinLossPieChart, 
  HorizonBarChart, 
  PerformanceTrendChart,
  SectorPerformanceChart,
  ScoreDistributionChart,
  AnalyticsDashboard 
} from './analysticsCharts';



const CredibilityChipsTab = ({ userId, isOwner = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userChips = [], loading = false, error = null } = useSelector(s => s.credibilityChips ?? {});
  const {
    userHistory = {
      posts: [],
      loading: false,
      error: null,
      hasMore: true,
      currentPage: 1,
      perPage: 10,
      sortBy: 'latest',
      period: 'all',
      total: null,
    },
    userGroupHistory = {
      posts: [],
      loading: false,
      error: null,
      hasMore: true,
      total: null,
    },
  } = useSelector(s => s.posts ?? {});
  
  const [activeView, setActiveView] = useState('predictions');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [horizonFilter, setHorizonFilter] = useState('all');
  const [expandedAnalysis, setExpandedAnalysis] = useState({});

  const credibilityClass = (val) => {
    if (val >= 200) return 'text-emerald-400';
    if (val >= 100) return 'text-amber-400';
    return 'text-rose-400';
  };
  
  const formatHorizon = (seconds) => {
    if (!seconds) return '—';
    if (seconds <= 0) return t('credibility.chipsTab.horizonExpired');
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return days > 0
      ? t('credibility.chipsTab.horizonDaysHours', { days, hours })
      : t('credibility.chipsTab.horizonHoursOnly', { hours });
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text) return '—';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserChips(userId));
      dispatch(fetchUserHistoryPosts({
        userId,
        skip: 0,
        limit: 20,
        sort_by: 'latest',
        period: 'all'
      }));
      // Separate fetch: GroupPost is a different table from Post, so a
      // user's group posts can never come back from the call above.
      dispatch(fetchUserGroupHistoryPosts({ userId, skip: 0, limit: 20 }));
      if (activeView === 'predictions') {
        logAnalyticsEvent('prediction_viewed', { viewed_user_id: userId });
      }
    }

  }, [dispatch, userId, activeView]);

  // Process chips data with error handling
  const processedChips = useMemo(() => {
    if (!Array.isArray(userChips)) return [];
    
    return userChips.map(chip => {
      const prediction = chip.prediction?.[0] || {};
      
      return {
        // Basic identification
        id: chip.post_id,
        postId: chip.post_id,
        
        // Content & metadata
        content: chip.content || '',
        createdAt: chip.created_at,
        sector: chip.sector_used || '—',
        
        // Credibility scores
        credibilityGlobal: Number(chip.credibility?.global || 0),
        credibilitySector: chip.credibility?.sector ? Number(chip.credibility.sector) : null,
        credibilityTier: chip.credibility?.tier || 'Beginner',
        
        // Prediction details
        predictionId: prediction.id,
        ticker: prediction.ticker || '—',
        direction: prediction.direction,
        confidence: Number(prediction.confidence || 0),
        horizonDays: prediction.horizon_days,
        deadlineAt: prediction.deadline_at,
        targetPct: prediction.target_pct,
        entryPrice: prediction.entry_price,
        predictionStatus: prediction.status || 'pending',
        horizonRemaining: prediction.horizon_remaining,
        outcome: prediction.outcome,
        
        // AI analysis data (cleaned)
        summary: chip.ai?.summary || '',
        sentiment: chip.ai?.sentiment || 'neutral',
        stance: chip.ai?.stance || 'neutral',
        entities: chip.ai?.entities || [],
        
        // Prediction structure from AI
        aiPrediction: chip.ai?.prediction?.prediction || null,
        isPrediction: chip.ai?.prediction?.is_prediction || false,
        // The real, scoring-relevant quality classification lives on the
        // Prediction row itself (chip.prediction[0].quality -- exactly what
        // credibility_service.py's HARD_PREDICTION_WEIGHT/SOFT_PREDICTION_WEIGHT
        // reads), not on the raw AI-enrichment blob (chip.ai.prediction),
        // which never actually carries a nested "quality" key.
        predictionQuality: prediction.quality || 'unknown',
        
        // Outcome metrics
        actualReturn: prediction.outcome?.actual_return_pct,
        accuracyScore: prediction.outcome?.accuracy_score,
        totalScore: prediction.outcome?.total_score,
        daysToOutcome: prediction.outcome?.days_to_outcome,
        wasCorrectDirection: prediction.outcome?.was_correct_direction,
        evaluatedAt: prediction.outcome?.evaluated_at,
        
        // Extract ticker from entities as fallback
        entityTicker: chip.ai?.entities?.find(e => e.type === 'ticker')?.value || 
                     (prediction.ticker ? `$${prediction.ticker}` : '—'),
      };
    });
  }, [userChips]);

  // Process post history data with proper field mapping
  const processedPostHistory = useMemo(() => {
    const regular = Array.isArray(userHistory.posts) ? userHistory.posts.map(post => ({
      id: post.id,
      isGroupPost: false,
      groupId: null,
      groupName: null,
      content: post.content || '',
      createdAt: post.created_at,
      updatedAt: post.updated_at,
      likes: post.likes || 0,
      bullCount: post.bull_count || 0,
      bearCount: post.bear_count || 0,
      commentCount: post.comment_count || post.comments?.length || 0,
      stocks: post.stocks || [],

      // Check if this post has associated predictions/analysis
      hasChipData: userChips.some(chip => chip.post_id === post.id),
      chipData: userChips.find(chip => chip.post_id === post.id),

      // Extract prediction info from chip data. Quality reads the real
      // Prediction row (chip.prediction[0].quality), not chip.ai.prediction
      // (the raw AI-enrichment blob, which never nests a "quality" key).
      isPrediction: userChips.find(chip => chip.post_id === post.id)?.ai?.prediction?.is_prediction || false,
      predictionQuality: userChips.find(chip => chip.post_id === post.id)?.prediction?.[0]?.quality,
      sentiment: userChips.find(chip => chip.post_id === post.id)?.ai?.sentiment,

      viewCount: post.view_count || 0,
    })) : [];

    // Group posts don't feed the credibility-chip pipeline (chips are keyed
    // off Post.id, not GroupPost.id, and a post/group_post id can collide),
    // so no chip/prediction lookup here -- just the group it belongs to.
    const group = Array.isArray(userGroupHistory.posts) ? userGroupHistory.posts.map(post => ({
      id: `group-${post.id}`,
      isGroupPost: true,
      groupId: post.group_id,
      groupName: post.group_name,
      content: post.content || '',
      createdAt: post.created_at,
      updatedAt: post.updated_at,
      likes: post.likes || 0,
      bullCount: post.bull_count || 0,
      bearCount: post.bear_count || 0,
      commentCount: post.comment_count || 0,
      stocks: [],
      hasChipData: false,
      chipData: null,
      isPrediction: false,
      predictionQuality: null,
      sentiment: null,
      viewCount: 0,
    })) : [];

    return [...regular, ...group].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [userHistory.posts, userGroupHistory.posts, userChips]);

  // Separate data by type
  const { predictions, signals, postHistoryData } = useMemo(() => {
    const preds = processedChips.filter(chip => chip.isPrediction === true);
    const sigs = processedChips.filter(chip => chip.isPrediction === false);
    const postHist = processedPostHistory;

    return { 
      predictions: preds, 
      signals: sigs, 
      postHistoryData: postHist
    };
  }, [processedChips, processedPostHistory]);

  // Enhanced filtering
const filteredData = useMemo(() => {
  let dataToFilter = [];
  
  switch (activeView) {
    case 'predictions':
      dataToFilter = predictions;
      break;
    case 'analysis':
      dataToFilter = predictions; // Analysis is based on predictions
      break;
    case 'postHistory':
      dataToFilter = postHistoryData;
      break;
    default:
      dataToFilter = [];
  }
  
  let filtered = dataToFilter;

  // Apply search filter
  if (searchTerm.trim()) {
    const searchLower = searchTerm.toLowerCase();
    filtered = filtered.filter(item => {
      if (activeView === 'postHistory') {
        return (
          item.content.toLowerCase().includes(searchLower) ||
          (item.chipData?.ai?.entities?.some(e => 
            e.value.toLowerCase().includes(searchLower)
          )) ||
          (item.stocks?.some(s => 
            s.symbol?.toLowerCase().includes(searchLower)
          ))
        );
      } else {
        return (
          (item.sector && item.sector.toLowerCase().includes(searchLower)) ||
          (item.ticker && item.ticker.toLowerCase().includes(searchLower)) ||
          (item.entityTicker && item.entityTicker.toLowerCase().includes(searchLower)) ||
          (item.summary && item.summary.toLowerCase().includes(searchLower))
        );
      }
    });
  }

  // Apply filters for predictions only
  if (activeView === 'predictions') {
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.predictionStatus === statusFilter);
    }
    if (horizonFilter !== 'all') {
      filtered = filtered.filter(item => {
        const horizonDays = item.horizonDays;
        if (!horizonDays) return horizonFilter === 'long';
        
        if (horizonFilter === 'short') return horizonDays <= 7;
        if (horizonFilter === 'medium') return horizonDays > 7 && horizonDays <= 30;
        if (horizonFilter === 'long') return horizonDays > 30;
        
        return true;
      });
    }
  }

  // Enhanced sorting
  return filtered.sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return sortDirection === 'asc' ? -1 : 1;
    if (bVal == null) return sortDirection === 'asc' ? 1 : -1;
    
    // Numeric fields
    if (['credibilityGlobal', 'likes', 'bullCount', 'bearCount', 'commentCount', 
         'confidence', 'totalScore', 'accuracyScore', 'viewCount'].includes(sortField)) {
      const aNum = Number(aVal) || 0;
      const bNum = Number(bVal) || 0;
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    }
    
    // Date fields
    if (['createdAt', 'deadlineAt', 'evaluatedAt'].includes(sortField)) {
      const aDate = new Date(aVal);
      const bDate = new Date(bVal);
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    }

    // String fields
    const aStr = String(aVal || '').toLowerCase();
    const bStr = String(bVal || '').toLowerCase();
    const comparison = aStr.localeCompare(bStr);
    return sortDirection === 'asc' ? comparison : -comparison;
  });
}, [predictions, postHistoryData, activeView, searchTerm, statusFilter, horizonFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleLoadMorePostHistory = () => {
    if (userId && userHistory.hasMore && !userHistory.loading) {
      dispatch(
        loadMoreUserHistoryPosts({
          userId,
          skip: userHistory.posts.length, // continue after current count
          limit: 20,                      // fetch next 20
          sort_by: 'latest',
          period: 'all',
        })
      );
    }
    if (userId && userGroupHistory.hasMore && !userGroupHistory.loading) {
      dispatch(loadMoreUserGroupHistoryPosts({ userId }));
    }
  };

  const handleViewPost = (post) => {
    if (post.isGroupPost) {
      navigate(`/groups/${post.groupId}`);
    } else {
      // Regular posts have no individual permalink page in this app today --
      // the main feed is the closest real "view" destination available.
      navigate('/stockers');
    }
  };


  const toggleAnalysis = (id) => {
    setExpandedAnalysis(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Helper functions
  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="w-4 h-4 text-amber-400" />,
      active: <Activity className="w-4 h-4 text-blue-400" />,
      won: <CheckCircle className="w-4 h-4 text-emerald-400" />,
      lost: <XCircle className="w-4 h-4 text-rose-400" />,
      expired: <Timer className="w-4 h-4 text-gray-500 dark:text-gray-400" />,
      canceled: <Minus className="w-4 h-4 text-gray-600 dark:text-gray-500" />
    };
    return icons[status] || icons.pending;
  };

  const getStatusStyle = (status) => {
    const styles = {
      pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      active: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      won: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      lost: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
      expired: 'bg-cream-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-300 border-cream-300 dark:border-gray-500/30',
      canceled: 'bg-cream-100 dark:bg-gray-600/20 text-gray-500 dark:text-gray-400 border-cream-300 dark:border-gray-600/30'
    };
    return styles[status] || styles.pending;
  };

  const getSentimentIcon = (sentiment) => {
    if (!sentiment || sentiment === 'unknown' || sentiment === 'neutral') {
      return <Minus className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
    }
    const lower = sentiment.toLowerCase();
    if (lower.includes('bull') || lower === 'positive') {
      return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    }
    if (lower.includes('bear') || lower === 'negative') {
      return <TrendingDown className="w-4 h-4 text-rose-400" />;
    }
    return <Target className="w-4 h-4 text-blue-400" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '—';
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '—';
      const now = new Date();
      const diff = now - date;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(hours / 24);
      
      if (days > 7) return t('credibility.chipsTab.timeWeeksAgo', { count: Math.floor(days / 7) });
      if (days > 0) return t('credibility.chipsTab.timeDaysAgo', { count: days });
      if (hours > 0) return t('credibility.chipsTab.timeHoursAgo', { count: hours });
      return t('credibility.chipsTab.timeNow');
    } catch {
      return '—';
    }
  };

  const getPredictionDetails = (chip) => {
    const aiPred = chip.aiPrediction;
    const pred = chip;
    
    if (aiPred) {
      const direction = aiPred.direction === 'up' ? '↗' : aiPred.direction === 'down' ? '↘' : '—';
      const magnitude = aiPred.magnitude?.percent ? `${aiPred.magnitude.percent}%` : '—';
      
      let timeframe = '—';
      if (aiPred.time_horizon?.value && aiPred.time_horizon?.unit) {
        timeframe = `${aiPred.time_horizon.value}${aiPred.time_horizon.unit.charAt(0)}`;
      }
      
      return { direction, magnitude, timeframe };
    }
    
    if (pred.direction) {
      const direction = pred.direction === 'up' ? '↗' : pred.direction === 'down' ? '↘' : '—';
      const magnitude = pred.targetPct ? `${pred.targetPct}%` : '—';
      const timeframe = pred.horizonDays ? `${pred.horizonDays}d` : '—';
      
      return { direction, magnitude, timeframe };
    }
    
    return null;
  };

  const SortButton = ({ field, children, className = "" }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center space-x-1 px-2 py-1 rounded-lg hover:bg-gray-600/30 transition-colors text-xs font-medium ${
        sortField === field ? 'text-blue-400' : 'text-gray-700 dark:text-gray-300'
      } ${className}`}
    >
      <span>{children}</span>
      {sortField === field && (
        sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />
      )}
    </button>
  );

  if (loading || userHistory.loading) {
    return (
      <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-cream-300 dark:border-gray-700/50 p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-cream-300 dark:border-gray-700/50 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-cream-900 dark:text-white flex items-center">
              <Shield className="w-6 h-6 mr-3 text-blue-400" />
              {t('credibility.chipsTab.title')}
              {isOwner && <span className="text-blue-400 ml-2">{t('credibility.chipsTab.yourActivity')}</span>}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {t('credibility.chipsTab.subtitle')}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-blue-600/20 to-blue-800/20 rounded-lg px-3 py-2 border border-blue-500/30">
              <div className="text-center">
                <div className="text-lg font-bold text-blue-400">{predictions.length}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.statPredictions')}</div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-purple-600/20 to-purple-800/20 rounded-lg px-3 py-2 border border-purple-500/30">
              <div className="text-center">
                <div className="text-lg font-bold text-purple-400">{predictions.length}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.statAnalysis')}</div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-orange-600/20 to-red-800/20 rounded-lg px-3 py-2 border border-orange-500/30">
              <div className="text-center">
                <div className="text-lg font-bold text-orange-400">{postHistoryData.length}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.statPosts')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Controls - Removed Posts tab, kept Post History */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center space-x-2 flex-wrap">
            {[
              { key: 'predictions', label: t('credibility.chipsTab.tabPredictions'), icon: Target, count: predictions.length, gradient: 'from-blue-600 to-blue-700' },
              { key: 'analysis', label: t('credibility.chipsTab.tabAnalysis'), icon: Activity, count: predictions.length, gradient: 'from-purple-600 to-purple-700' },
              { key: 'postHistory', label: t('credibility.chipsTab.tabPostHistory'), icon: Brain, count: postHistoryData.length, gradient: 'from-orange-600 to-red-700' }
            ].map(({ key, label, icon: Icon, count, gradient }) => (
              <button
                key={key}
                onClick={() => setActiveView(key)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                  activeView === key
                    ? `bg-gradient-to-r ${gradient} text-white shadow-lg`
                    : 'bg-cream-200 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-600/50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
                <div className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                  {count}
                </div>
              </button>
            ))}
          </div>

          <div className="flex items-center space-x-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
              <input
                type="text"
                placeholder={
                  activeView === 'postHistory'
                    ? t('credibility.chipsTab.searchPlaceholderPostHistory')
                    : t('credibility.chipsTab.searchPlaceholderDefault')
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-cream-200 dark:bg-gray-700/50 border border-cream-300 dark:border-gray-600/50 rounded-lg text-cream-900 dark:text-white placeholder-cream-700 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48 text-sm"
              />
            </div>
            {activeView === 'predictions' && (
              <>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label={t('credibility.chipsTab.filterByStatus')}
                  className="px-3 py-2 bg-cream-200 dark:bg-gray-700/50 border border-cream-300 dark:border-gray-600/50 rounded-lg text-cream-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="all">{t('credibility.chipsTab.statusAll')}</option>
                  <option value="pending">{t('credibility.chipsTab.statusPending')}</option>
                  <option value="active">{t('credibility.chipsTab.statusActive')}</option>
                  <option value="won">{t('credibility.chipsTab.statusWon')}</option>
                  <option value="lost">{t('credibility.chipsTab.statusLost')}</option>
                  <option value="expired">{t('credibility.chipsTab.statusExpired')}</option>
                </select>

                <select
                  value={horizonFilter}
                  onChange={(e) => setHorizonFilter(e.target.value)}
                  aria-label={t('credibility.chipsTab.filterByHorizon')}
                  className="px-3 py-2 bg-cream-200 dark:bg-gray-700/50 border border-cream-300 dark:border-gray-600/50 rounded-lg text-cream-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="all">{t('credibility.chipsTab.horizonAll')}</option>
                  <option value="short">{t('credibility.chipsTab.horizonShort')}</option>
                  <option value="medium">{t('credibility.chipsTab.horizonMedium')}</option>
                  <option value="long">{t('credibility.chipsTab.horizonLong')}</option>
                </select>
              </>
            )}
          </div>
        </div>
      </div>

      {(error || userHistory.error) && (
        <div className="bg-rose-900/50 border-l-4 border-rose-500 p-4 rounded-xl">
          <p className="text-rose-200 text-sm">
            {t('credibility.chipsTab.errorLoadingData', { error: error || userHistory.error })}
          </p>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-cream-300 dark:border-gray-700/50 overflow-hidden">
        <div className="p-4 border-b border-cream-300 dark:border-gray-700/50">
          <h3 className="text-lg font-semibold text-cream-900 dark:text-white flex items-center">
            {activeView === 'predictions' ? (
              <>
                <Target className="w-5 h-5 mr-2 text-blue-400" />
                {t('credibility.chipsTab.tabPredictions')} ({filteredData.length})
              </>
            ) : activeView === 'analysis' ? (
              <>
                <Activity className="w-5 h-5 mr-2 text-purple-400" />
                {t('credibility.chipsTab.tabAnalysis')} ({predictions.length})
              </>
            ) : (
              <>
                <Brain className="w-5 h-5 mr-2 text-orange-400" />
                {t('credibility.chipsTab.tabPostHistory')} ({filteredData.length})
                {userHistory.total && (
                  <span className="text-gray-500 dark:text-gray-400 text-sm ml-2">
                    {t('credibility.common.ofTotal', { total: userHistory.total })}
                  </span>
                )}
              </>
            )}
          </h3>
        </div>

        {/* Analysis Tab */}
        {activeView === 'analysis' ? (
          predictions.length === 0 ? (
            <div className="p-12 text-center">
              <Activity className="w-12 h-12 text-gray-500 dark:text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-lg">{t('credibility.chipsTab.noAnalysis')}</p>
              <p className="text-gray-600 dark:text-gray-500 text-sm mt-2">
                {t('credibility.chipsTab.noAnalysisHint')}
              </p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              <AnalyticsDashboard predictions={predictions} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-4">
                  <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('credibility.chipsTab.winLoss')}</h6>
                  <WinLossPieChart predictions={predictions} />
                </div>
                <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-4">
                  <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('credibility.chipsTab.horizonBreakdown')}</h6>
                  <HorizonBarChart predictions={predictions} />
                </div>
                <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-4 md:col-span-2">
                  <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('credibility.chipsTab.performanceTrend')}</h6>
                  <PerformanceTrendChart predictions={predictions} />
                </div>
                <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-4">
                  <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('credibility.chipsTab.sectorPerformance')}</h6>
                  <SectorPerformanceChart predictions={predictions} />
                </div>
                <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-4">
                  <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('credibility.chipsTab.scoreDistribution')}</h6>
                  <ScoreDistributionChart predictions={predictions} />
                </div>
              </div>
            </div>
          )
        ) : filteredData.length === 0 ? (
          <div className="p-12 text-center">
            {activeView === 'predictions' ? (
              <Target className="w-12 h-12 text-gray-500 dark:text-gray-400 mx-auto mb-4" />
            ) : (
              <Brain className="w-12 h-12 text-gray-500 dark:text-gray-400 mx-auto mb-4" />
            )}
            <p className="text-gray-500 dark:text-gray-400 text-lg">{t('credibility.chipsTab.noItemsFound', { view: t(`credibility.chipsTab.viewNames.${activeView}`) })}</p>
            <p className="text-gray-600 dark:text-gray-500 text-sm mt-2">
              {searchTerm || statusFilter !== 'all' || horizonFilter !== 'all'
                ? t('credibility.chipsTab.tryAdjustingFilters')
                : t('credibility.chipsTab.startCreating', { view: t(`credibility.chipsTab.viewNames.${activeView}`) })}
            </p>
          </div>
        ) : activeView === 'postHistory' ? (
          /* Post History Table */
          <div className="overflow-x-auto">
            <div className="min-w-full">
              <table className="w-full">
                <thead className="bg-cream-100 dark:bg-gray-700/30">
                  <tr>
                    <th className="px-4 py-3 text-left min-w-[300px]">
                      <SortButton field="content">{t('credibility.chipsTab.colContent')}</SortButton>
                    </th>
                    <th className="px-4 py-3 text-left min-w-[120px]">
                      {t('credibility.chipsTab.colType')}
                    </th>
                    <th className="px-4 py-3 text-left min-w-[120px]">
                      {t('credibility.chipsTab.colGroup')}
                    </th>
                    <th className="px-4 py-3 text-left min-w-[100px]">
                      <SortButton field="createdAt">{t('credibility.chipsTab.colDate')}</SortButton>
                    </th>
                    <th className="px-4 py-3 text-left min-w-[100px]">
                      {t('credibility.chipsTab.colSentiment')}
                    </th>
                    <th className="px-4 py-3 text-left min-w-[120px]">
                      <SortButton field="likes">{t('credibility.chipsTab.colEngagement')}</SortButton>
                    </th>
                    <th className="px-4 py-3 text-left min-w-[100px]">
                      {t('credibility.chipsTab.colTickers')}
                    </th>
                    <th className="px-4 py-3 text-left min-w-[100px]">
                      {t('credibility.chipsTab.colPerformance')}
                    </th>
                    <th className="px-4 py-3 text-left min-w-[80px]">
                      {t('credibility.chipsTab.colActions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-300 dark:divide-gray-700/50">
                  {filteredData.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-700/30 transition-colors">
                      <td className="px-4 py-4">
                        <div className="space-y-2 max-w-sm">
                          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                            {truncateText(post.content, 120)}
                          </p>
                          {post.hasChipData && (
                            <div className="flex items-center space-x-2">
                              <div className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded text-xs">
                                {t('credibility.chipsTab.aiAnalyzed')}
                              </div>
                              {post.chipData?.credibility?.global && (
                                <div className={`px-2 py-1 rounded text-xs ${credibilityClass(post.chipData.credibility.global)} bg-current/20`}>
                                  {Number(post.chipData.credibility.global).toFixed(1)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col space-y-1">
                          {post.isPrediction ? (
                            <div className="flex items-center space-x-1">
                              <Target className="w-4 h-4 text-blue-400 flex-shrink-0" />
                              <span className="text-blue-300 text-xs font-medium">{t('credibility.chipsTab.typePrediction')}</span>
                            </div>
                          ) : post.hasChipData ? (
                            <div className="flex items-center space-x-1">
                              <Activity className="w-4 h-4 text-purple-400 flex-shrink-0" />
                              <span className="text-purple-300 text-xs font-medium">{t('credibility.chipsTab.typeAnalysis')}</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                              <span className="text-gray-500 dark:text-gray-400 text-xs">{t('credibility.chipsTab.typePost')}</span>
                            </div>
                          )}
                          {post.predictionQuality && (
                            <span
                              className={`text-xs px-2 py-1 rounded w-fit ${
                                post.predictionQuality === 'hard'
                                  ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                                  : 'bg-amber-500/20 text-amber-700 dark:text-amber-400'
                              }`}
                              title={
                                post.predictionQuality === 'hard'
                                  ? t('credibility.chipsTab.qualityHardTooltip')
                                  : t('credibility.chipsTab.qualitySoftTooltip')
                              }
                            >
                              {post.predictionQuality}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {post.isGroupPost ? (
                          <button
                            onClick={() => navigate(`/groups/${post.groupId}`)}
                            className="flex items-center space-x-1 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 px-2 py-1 rounded text-xs font-medium transition-colors w-fit"
                            title={t('credibility.chipsTab.openGroupTitle', { groupName: post.groupName })}
                          >
                            <Users className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate max-w-[100px]">{post.groupName || t('credibility.chipsTab.groupFallback')}</span>
                          </button>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-500 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-300 text-sm">
                        <div>
                          <div title={formatDate(post.createdAt)}>
                            {formatTimeAgo(post.createdAt)}
                          </div>
                          {post.updatedAt && post.updatedAt !== post.createdAt && (
                            <div className="text-xs text-gray-600 dark:text-gray-500 mt-1">
                              {t('credibility.chipsTab.updatedPrefix', { time: formatTimeAgo(post.updatedAt) })}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          {getSentimentIcon(post.sentiment)}
                          <span className="text-gray-700 dark:text-gray-300 capitalize text-sm">
                            {post.sentiment || t('credibility.chipsTab.neutral')}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="grid grid-cols-2 gap-1 text-sm">
                          <div className="flex items-center space-x-1">
                            <Heart className="w-3 h-3 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-300">{post.likes}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MessageSquare className="w-3 h-3 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-300">{post.commentCount}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <TrendingUpIcon className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-300">{post.bullCount}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <TrendingDownIcon className="w-3 h-3 text-rose-400 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-300">{post.bearCount}</span>
                          </div>
                        </div>
                        {post.viewCount > 0 && (
                          <div className="flex items-center space-x-1 mt-1 text-xs text-gray-600 dark:text-gray-500">
                            <Eye className="w-3 h-3 flex-shrink-0" />
                            <span>{post.viewCount}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          {post.stocks?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {post.stocks.slice(0, 2).map(stock => (
                                <span key={stock.id} className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded whitespace-nowrap">
                                  {stock.symbol || stock.ticker}
                                </span>
                              ))}
                              {post.stocks.length > 2 && (
                                <span className="text-gray-500 dark:text-gray-400 text-xs">+{post.stocks.length - 2}</span>
                              )}
                            </div>
                          ) : post.chipData?.ai?.entities ? (
                            <div className="flex flex-wrap gap-1">
                              {post.chipData.ai.entities
                                .filter(e => e.type === 'ticker')
                                .slice(0, 2)
                                .map((entity, idx) => (
                                  <span key={idx} className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded whitespace-nowrap">
                                    {entity.value}
                                  </span>
                                ))}
                            </div>
                          ) : (
                            <span className="text-gray-600 dark:text-gray-500 text-sm">—</span>
                          )}
                          {post.chipData?.sector_used && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {post.chipData.sector_used}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {post.chipData?.prediction?.[0]?.outcome ? (
                          <div className="space-y-1">
                            <div className={`flex items-center space-x-1 ${
                              post.chipData.prediction[0].outcome.was_correct_direction 
                                ? 'text-emerald-400' 
                                : 'text-rose-400'
                            }`}>
                              {post.chipData.prediction[0].outcome.was_correct_direction ? (
                                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                              ) : (
                                <XCircle className="w-4 h-4 flex-shrink-0" />
                              )}
                              <span className="text-sm font-medium">
                                {post.chipData.prediction[0].outcome.was_correct_direction ? t('credibility.chipsTab.won') : t('credibility.chipsTab.lost')}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                              <div>{(post.chipData.prediction[0].outcome.actual_return_pct || 0).toFixed(1)}%</div>
                              <div>{post.chipData.prediction[0].outcome.total_score || 0}/100</div>
                            </div>
                          </div>
                        ) : post.isPrediction ? (
                          <div className="flex items-center space-x-1 text-amber-400">
                            <Timer className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm">{t('credibility.chipsTab.pending')}</span>
                          </div>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-500 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col space-y-1">
                          <button
                            onClick={() => handleViewPost(post)}
                            className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 transition-colors text-xs w-fit"
                            aria-label={post.isGroupPost ? t('credibility.chipsTab.viewPostAriaGroup', { groupName: post.groupName }) : t('credibility.chipsTab.viewPostAria')}
                          >
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            <span>{t('credibility.chipsTab.viewLabel')}</span>
                          </button>
                          {post.hasChipData && (
                            <button
                              onClick={() => toggleAnalysis(post.id)}
                              className="flex items-center space-x-1 text-purple-400 hover:text-purple-300 transition-colors text-xs w-fit"
                            >
                              <Brain className="w-3 h-3 flex-shrink-0" />
                              <span>{t('credibility.chipsTab.analysisLabel')}</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Load More Button for Post History -- covers both regular
                posts and group posts, which paginate independently */}
            {activeView === 'postHistory' && (userHistory.hasMore || userGroupHistory.hasMore) && (
              <div className="p-4 border-t border-cream-300 dark:border-gray-700/50 text-center">
                <button
                  onClick={handleLoadMorePostHistory}
                  disabled={userHistory.loading || userGroupHistory.loading}
                  className="px-6 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white rounded-lg transition-colors font-medium text-sm flex items-center space-x-2 mx-auto"
                >
                  {(userHistory.loading || userGroupHistory.loading) ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>{t('credibility.chipsTab.loadingLabel')}</span>
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4" />
                      <span>{t('credibility.chipsTab.loadMorePosts')}</span>
                    </>
                  )}
                </button>
                {(userHistory.total || userGroupHistory.total) && (
                  <p className="text-gray-500 dark:text-gray-400 text-xs mt-2">
                    {t('credibility.chipsTab.showingOfTotal', { shown: postHistoryData.length, total: (userHistory.total || 0) + (userGroupHistory.total || 0) })}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (

          /* Predictions Table */
          <div className="overflow-x-auto">
            <div className="min-w-full">
              <table className="w-full">
                <thead className="bg-cream-100 dark:bg-gray-700/30">
                  <tr>
                    <th className="px-4 py-3 text-left min-w-[100px]">
                      <SortButton field="ticker">{t('credibility.chipsTab.colTicker')}</SortButton>
                    </th>
                    <th className="px-4 py-3 text-left min-w-[120px]">
                      <SortButton field="sector">{t('credibility.chipsTab.colSector')}</SortButton>
                    </th>
                    {activeView === 'predictions' && (
                      <>
                        <th className="px-4 py-3 text-left min-w-[100px]">
                          <SortButton field="predictionStatus">{t('credibility.chipsTab.colStatus')}</SortButton>
                        </th>
                        <th className="px-4 py-3 text-left min-w-[120px]">{t('credibility.chipsTab.colPrediction')}</th>
                        <th className="px-4 py-3 text-left min-w-[100px]">{t('credibility.chipsTab.colHorizon')}</th>
                        <th className="px-4 py-3 text-left min-w-[120px]">{t('credibility.chipsTab.colPerformance')}</th>
                      </>
                    )}
                    <th className="px-4 py-3 text-left min-w-[100px]">
                      <SortButton field="sentiment">{t('credibility.chipsTab.colSentiment')}</SortButton>
                    </th>
                    <th className="px-4 py-3 text-left min-w-[100px]">
                      <SortButton field="credibilityGlobal">{t('credibility.chipsTab.colScore')}</SortButton>
                    </th>
                    <th className="px-4 py-3 text-left min-w-[200px]">{t('credibility.chipsTab.colSummary')}</th>
                    <th className="px-4 py-3 text-left min-w-[100px]">
                      <SortButton field="createdAt">{t('credibility.chipsTab.colDate')}</SortButton>
                    </th>
                    <th className="px-4 py-3 text-left min-w-[80px]">{t('credibility.chipsTab.colActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-300 dark:divide-gray-700/50">
                  {filteredData.map((item) => {
                    const predDetails = getPredictionDetails(item);
                    const isAnalysisExpanded = expandedAnalysis[item.id];
                    
                    return (
                      <tr key={item.id} className="hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              activeView === 'predictions' ? 'bg-blue-400' : 'bg-purple-400'
                            }`}></div>
                            <span className="text-cream-900 dark:text-white font-mono font-bold">
                              {item.ticker || item.entityTicker}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-gray-700 dark:text-gray-300">
                          <div className="truncate max-w-[120px]" title={item.sector}>
                            {item.sector}
                          </div>
                        </td>
                        {activeView === 'predictions' && (
                          <>
                            <td className="px-4 py-4">
                              <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs border ${getStatusStyle(item.predictionStatus)}`}>
                                {getStatusIcon(item.predictionStatus)}
                                <span className="capitalize">{item.predictionStatus}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              {predDetails ? (
                                <div className="text-sm space-y-1">
                                  <div className="flex items-center space-x-2 text-cream-900 dark:text-white">
                                    <span className="text-lg">{predDetails.direction}</span>
                                    <span className="font-bold">{predDetails.magnitude}</span>
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {t('credibility.chipsTab.inTimeframe', { timeframe: predDetails.timeframe })}
                                  </div>
                                  {item.entryPrice && (
                                    <div className="text-xs text-gray-600 dark:text-gray-500">
                                      {t('credibility.chipsTab.entryPrefix', { price: item.entryPrice })}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-500 dark:text-gray-400 text-sm">—</span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <div className="space-y-1">
                                <div className="text-gray-700 dark:text-gray-300 text-sm">
                                  {formatHorizon(item.horizonRemaining)}
                                </div>
                                {item.deadlineAt && (
                                  <div className="text-xs text-gray-600 dark:text-gray-500">
                                    {formatDate(item.deadlineAt)}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              {item.outcome ? (
                                <div className="space-y-1">
                                  <div className={`flex items-center space-x-1 text-sm ${
                                    item.wasCorrectDirection ? 'text-emerald-400' : 'text-rose-400'
                                  }`}>
                                    {item.wasCorrectDirection ? (
                                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                                    ) : (
                                      <XCircle className="w-4 h-4 flex-shrink-0" />
                                    )}
                                    <span className="font-medium">
                                      {item.wasCorrectDirection ? t('credibility.chipsTab.won') : t('credibility.chipsTab.lost')}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                    <div>{(item.actualReturn || 0).toFixed(1)}%</div>
                                    <div className="flex items-center space-x-1">
                                      <Award className="w-3 h-3 flex-shrink-0" />
                                      <span>{item.totalScore || 0}/100</span>
                                    </div>
                                    {item.daysToOutcome && (
                                      <div>{item.daysToOutcome}d</div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-1 text-amber-400">
                                  <Timer className="w-4 h-4 flex-shrink-0" />
                                  <span className="text-sm">{t('credibility.chipsTab.pending')}</span>
                                </div>
                              )}
                            </td>
                          </>
                        )}
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            {getSentimentIcon(item.sentiment)}
                            <span className="text-gray-700 dark:text-gray-300 capitalize text-sm">
                              {item.sentiment}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <div>
                              <span className={`font-bold ${credibilityClass(item.credibilityGlobal)}`}>
                                {Number(item.credibilityGlobal).toFixed(1)}
                              </span>
                              {item.credibilitySector && (
                                <span className="text-gray-500 dark:text-gray-400 ml-1">
                                  /{Number(item.credibilitySector).toFixed(1)}
                                </span>
                              )}
                            </div>
                            {item.confidence > 0 && (
                              <div className="flex items-center space-x-1">
                                <div className="w-8 bg-cream-300 dark:bg-gray-600 rounded-full h-1">
                                  <div 
                                    className="bg-blue-400 h-1 rounded-full"
                                    style={{ width: `${item.confidence * 100}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {Math.round(item.confidence * 100)}%
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-2 max-w-[200px]">
                            <p className="text-gray-700 dark:text-gray-300 text-sm line-clamp-2">
                              {truncateText(item.summary || item.content, 100)}
                            </p>
                            {item.entities && item.entities.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {item.entities.slice(0, 3).map((entity, idx) => (
                                  <span key={idx} className="bg-blue-500/20 text-blue-700 dark:text-blue-400 text-xs px-1.5 py-0.5 rounded">
                                    {entity.value}
                                  </span>
                                ))}
                                {item.entities.length > 3 && (
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">+{item.entities.length - 3}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-gray-700 dark:text-gray-300 text-sm">
                          <div className="space-y-1">
                            <div>{formatDate(item.createdAt)}</div>
                            <div className="text-xs text-gray-600 dark:text-gray-500">
                              {formatTimeAgo(item.createdAt)}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col space-y-1">
                            {item.postId && (
                              <button
                                className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 transition-colors text-xs w-fit"
                                aria-label={t('credibility.chipsTab.viewPostAria')}
                              >
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                <span>{t('credibility.chipsTab.viewLabel')}</span>
                              </button>
                            )}
                            <button
                              onClick={() => toggleAnalysis(item.id)}
                              className="flex items-center space-x-1 text-purple-400 hover:text-purple-300 transition-colors text-xs w-fit"
                            >
                              <Brain className="w-3 h-3 flex-shrink-0" />
                              <span>{isAnalysisExpanded ? t('credibility.chipsTab.hideLabel') : t('credibility.chipsTab.showLabel')}</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

        {/* Enhanced Analysis Panels */}
        {Object.keys(expandedAnalysis).some(key => expandedAnalysis[key]) && (
          <div className="space-y-4">
            {filteredData.filter(item => expandedAnalysis[item.id]).map(item => (
              <div
                key={`analysis-${item.id}`}
                className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-cream-300 dark:border-gray-700/50 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-cream-900 dark:text-white flex items-center">
                    <Brain className="w-5 h-5 mr-2 text-orange-400" />
                    {t('credibility.chipsTab.analysisFor', { ticker: item.ticker || item.entityTicker })}
                  </h4>
                  <button
                    onClick={() => toggleAnalysis(item.id)}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* LEFT COLUMN */}
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('credibility.chipsTab.summaryLabel')}</h5>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        {item.summary || t('credibility.chipsTab.noSummary')}
                      </p>
                    </div>

                    <div>
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('credibility.chipsTab.analysisDetailsLabel')}</h5>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            {getSentimentIcon(item.sentiment)}
                            <span className="text-gray-700 dark:text-gray-300 capitalize">{item.sentiment}</span>
                          </div>
                          {item.stance && item.stance !== item.sentiment && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.stancePrefix', { stance: item.stance })}</div>
                          )}
                        </div>

                        <div
                          className="text-sm text-gray-500 dark:text-gray-400"
                          title={
                            item.predictionQuality === 'hard'
                              ? t('credibility.chipsTab.qualityHardFullTooltip')
                              : item.predictionQuality === 'soft'
                              ? t('credibility.chipsTab.qualitySoftFullTooltip')
                              : undefined
                          }
                        >
                          {t('credibility.chipsTab.qualityPrefix', { quality: item.predictionQuality || t('credibility.chipsTab.unknown') })}
                        </div>

                        {item.confidence > 0 && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.confidenceLabel')}</span>
                            <div className="flex items-center space-x-2">
                              <div className="w-16 bg-cream-300 dark:bg-gray-600 rounded-full h-2">
                                <div
                                  className="bg-blue-400 h-2 rounded-full"
                                  style={{ width: `${item.confidence * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {Math.round(item.confidence * 100)}%
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {item.entities?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('credibility.chipsTab.detectedEntities')}</h5>
                        <div className="flex flex-wrap gap-2">
                          {item.entities.map((entity, idx) => (
                            <span
                              key={idx}
                              className="bg-blue-500/20 text-blue-700 dark:text-blue-400 text-xs px-2 py-1 rounded border border-blue-500/30"
                            >
                              {entity.value} ({entity.type})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Enhanced Mini Charts Section */}
                    <div className="space-y-4">
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('credibility.chipsTab.contextualInsights')}</h5>
                      <div className="grid grid-cols-1 gap-4">

                        {/* Similar Predictions Performance */}
                        <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-4">
                          <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                            <Target className="w-3 h-3 mr-1 text-blue-400" />
                            {t('credibility.chipsTab.similarSectorPerformance')}
                          </h6>
                          <div className="flex justify-center">
                            <WinLossPieChart
                              predictions={predictions.filter(p => p.sector === item.sector)}
                              width={180}
                              height={180}
                            />
                          </div>
                          <div className="text-center mt-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {t('credibility.chipsTab.predictionsInSector', { count: predictions.filter(p => p.sector === item.sector).length, sector: item.sector })}
                            </span>
                          </div>
                        </div>

                        {/* Individual Prediction Timeline Context */}
                        {activeView === 'predictions' && predictions.filter(p => p.outcome).length > 1 && (
                          <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-4">
                            <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                              <TrendingUp className="w-3 h-3 mr-1 text-purple-400" />
                              {t('credibility.chipsTab.performanceTrendContext')}
                            </h6>
                            <PerformanceTrendChart
                              predictions={predictions.filter(p => p.outcome)}
                              width={280}
                              height={140}
                            />
                          </div>
                        )}

                        {/* Horizon Analysis for this prediction type */}
                        <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-4">
                          <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                            <Clock className="w-3 h-3 mr-1 text-amber-400" />
                            {t('credibility.chipsTab.horizonAnalysis')}
                          </h6>
                          <HorizonBarChart
                            predictions={predictions}
                            width={280}
                            height={120}
                          />
                        </div>

                        {/* Score context if this prediction has a score */}
                        {item.totalScore && (
                          <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-4">
                            <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                              <Award className="w-3 h-3 mr-1 text-orange-400" />
                              {t('credibility.chipsTab.scoreDistributionContext')}
                            </h6>
                            <ScoreDistributionChart 
                              predictions={predictions.filter(p => p.totalScore)} 
                              width={280} 
                              height={120} 
                            />
                            <div className="text-center mt-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {t('credibility.chipsTab.yourScore')} <span className={`font-bold ${
                                  item.totalScore >= 80 ? 'text-emerald-400' :
                                  item.totalScore >= 60 ? 'text-amber-400' : 'text-rose-400'
                                }`}>
                                  {item.totalScore}/100
                                </span>
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT COLUMN */}
                  <div className="space-y-4">
                    {/* Performance Results */}
                    {activeView === 'predictions' && item.outcome && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('credibility.chipsTab.performanceResults')}</h5>
                        <div className="space-y-3">
                          <div
                            className={`flex items-center space-x-2 ${
                              item.wasCorrectDirection ? 'text-emerald-400' : 'text-rose-400'
                            }`}
                          >
                            {item.wasCorrectDirection ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <XCircle className="w-5 h-5" />
                            )}
                            <span className="font-medium">
                              {item.wasCorrectDirection ? t('credibility.chipsTab.predictionWon') : t('credibility.chipsTab.predictionLost')}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.actualReturn')}</div>
                              <div className={`text-cream-900 dark:text-white font-mono ${
                                (item.actualReturn || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                              }`}>
                                {(item.actualReturn || 0) >= 0 ? '+' : ''}{(item.actualReturn || 0).toFixed(2)}%
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.totalScore')}</div>
                              <div className={`font-mono ${
                                (item.totalScore || 0) >= 80 ? 'text-emerald-400' :
                                (item.totalScore || 0) >= 60 ? 'text-amber-400' : 'text-rose-400'
                              }`}>
                                {item.totalScore || 0}/100
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.accuracyScore')}</div>
                              <div className="text-cream-900 dark:text-white font-mono">
                                {(item.accuracyScore || 0).toFixed(1)}%
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.daysToOutcome')}</div>
                              <div className="text-cream-900 dark:text-white font-mono">{item.daysToOutcome || '—'}</div>
                            </div>
                          </div>

                          {item.evaluatedAt && (
                            <div className="text-xs text-gray-600 dark:text-gray-500">
                              {t('credibility.chipsTab.evaluatedOn', { date: formatDate(item.evaluatedAt) })}
                            </div>
                          )}

                          {/* Performance comparison */}
                          <div className="bg-cream-100 dark:bg-gray-700/30 rounded-lg p-3 mt-4">
                            <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('credibility.chipsTab.performanceVsAverages')}</h6>
                            <div className="space-y-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.yourReturn')}</span>
                                <span className={`font-mono ${
                                  (item.actualReturn || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                                }`}>
                                  {(item.actualReturn || 0) >= 0 ? '+' : ''}{(item.actualReturn || 0).toFixed(1)}%
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.avgReturn')}</span>
                                <span className="text-gray-700 dark:text-gray-300 font-mono">
                                  {(() => {
                                    const completedPreds = predictions.filter(p => p.actualReturn !== undefined);
                                    const avgReturn = completedPreds.length > 0
                                      ? completedPreds.reduce((sum, p) => sum + (p.actualReturn || 0), 0) / completedPreds.length
                                      : 0;
                                    return avgReturn >= 0 ? '+' : '' + avgReturn.toFixed(1) + '%';
                                  })()}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.sectorAvg')}</span>
                                <span className="text-gray-700 dark:text-gray-300 font-mono">
                                  {(() => {
                                    const sectorPreds = predictions.filter(p => p.sector === item.sector && p.actualReturn !== undefined);
                                    const sectorAvg = sectorPreds.length > 0
                                      ? sectorPreds.reduce((sum, p) => sum + (p.actualReturn || 0), 0) / sectorPreds.length
                                      : 0;
                                    return sectorAvg >= 0 ? '+' : '' + sectorAvg.toFixed(1) + '%';
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Credibility Scores */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('credibility.chipsTab.credibilityScores')}</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('credibility.chipsTab.globalScore')}</span>
                          <span className={`font-bold ${credibilityClass(item.credibilityGlobal)}`}>
                            {Number(item.credibilityGlobal).toFixed(1)}
                          </span>
                        </div>

                        {item.credibilitySector && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700 dark:text-gray-300">{t('credibility.chipsTab.sectorScore')}</span>
                            <span className={`font-bold ${credibilityClass(item.credibilitySector)}`}>
                              {Number(item.credibilitySector).toFixed(1)}
                            </span>
                          </div>
                        )}
                        
                        {/* Credibility trend indicator -- real tier from the backend
                            (derive_status_tier(), same 5-tier system as the profile
                            page), not a locally-fabricated threshold. This badge used
                            to compute its own "Expert/Intermediate/Beginner" labels
                            from a made-up >=200/>=100 scale unrelated to the real
                            ~900-1000 point system, showing a wrong tier right next to
                            real credibility data. */}
                        <div className="bg-cream-100 dark:bg-gray-700/30 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.scoreTier')}</span>
                            <span className={`text-xs font-medium px-2 py-1 rounded ${tierColors(item.credibilityTier).bg} ${tierColors(item.credibilityTier).text}`}>
                              {item.credibilityTier}
                            </span>
                          </div>
                          <div className="w-full bg-cream-300 dark:bg-gray-600 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all bg-gradient-to-r ${tierColors(item.credibilityTier).gradient}`}
                              style={{
                                width: `${Math.min((item.credibilityGlobal / 1000) * 100, 100)}%`
                              }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-500 mt-1">
                            <span>0</span>
                            <span>1000+</span>
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-600 dark:text-gray-500">
                          <div>{t('credibility.chipsTab.sectorLabel', { sector: item.sector })}</div>
                          <div className="mt-1">
                            {t('credibility.chipsTab.basedOnScored', { count: predictions.filter(p => p.credibilityGlobal).length })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Context */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('credibility.chipsTab.additionalContext')}</h5>
                      <div className="space-y-2 text-xs">
                        <div className="bg-cream-100 dark:bg-gray-700/30 rounded-lg p-3">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.predictionDate')}</span>
                            <span className="text-gray-700 dark:text-gray-300">{formatDate(item.createdAt)}</span>
                          </div>
                          {item.deadlineAt && (
                            <div className="flex justify-between mb-1">
                              <span className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.deadline')}</span>
                              <span className="text-gray-700 dark:text-gray-300">{formatDate(item.deadlineAt)}</span>
                            </div>
                          )}
                          {item.evaluatedAt && (
                            <div className="flex justify-between mb-1">
                              <span className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.evaluated')}</span>
                              <span className="text-gray-700 dark:text-gray-300">{formatDate(item.evaluatedAt)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('credibility.chipsTab.statusLabel')}</span>
                            <span className={`capitalize ${
                              item.predictionStatus === 'won' ? 'text-emerald-400' :
                              item.predictionStatus === 'lost' ? 'text-rose-400' :
                              item.predictionStatus === 'pending' ? 'text-amber-400' :
                              'text-gray-500 dark:text-gray-400'
                            }`}>
                              {item.predictionStatus}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}


    </div>
  );
};

export default CredibilityChipsTab;